import React from "react";
import { connect } from "react-redux";
import { Dropdown, Grid, Loader, Dimmer, Button } from "semantic-ui-react";
import DownloadButton from "@buttons/DownloadButton";
import {
  getGameTitleLibraryRaw,
  getManufacturersRaw,
  updateGameTitle,
  loadGameTitle,
} from "@actions/index";
import {
  gameTitleLibraryDownload,
  getAssetGameLibrary,
  getAssetsDetailsDownload,
} from "@api/egm";
import { setCreateAssetModal, showSuccessAlert } from "@actions/modals";
import AssetMainTable from "./AssetMainTable";
import BaseSettings from "../systemAdmin/BaseSettings";
import OptionsList from "../systemAdmin/OptionsList";
import AssetDetailView from "./AssetDetailView";
import { ASSET_STATUS_FILTER_DATA } from "@helpers/constant";
import { handleDownload } from "@helpers/downloads";

const GAME_LIBRARY_TABLE_COLUMNS = [
  { key: "code", label: "Code", accessor: "gameTitleCode" },
  { key: "name", label: "Name", accessor: "gameTitleName" },
  { key: "manufacturer", label: "Manufacturer", accessor: "manufacturerName" },
  { key: "category", label: "Category", accessor: "categoryName" },
  { key: "rtp", label: "Theo RTP%", accessor: "rtp" },
  {
    key: "installed",
    label: "Installed",
    accessor: (row) => row.installedCount ?? "-",
  },
  {
    key: "notInstalled",
    label: "Not Installed",
    accessor: (row) => row.notInstalledCount ?? "-",
  },
  { key: "pendingScrapped", label: "Pending Scrapped", accessor: "pendingScrappedCount" },
  { key: "sold", label: "Sold", accessor: "soldCount" },
];

const UPDATED_FILTER_OPTIONS = [
  { key: "all", value: "ALL", text: "ALL" },
  { key: "notSpecified", value: 0, text: "Name not specified" },
  { key: "updated", value: 1, text: "Updated" },
];

class GameLibraryView extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      operatorId: null,
      gameTitles: [],
      manufacturers: [],
      selectedUpdatedFilter: "ALL",
      selectedManufacturerId: "ALL",
      loading: false,
      selectedGameTitle: null,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      selectedNode: null,
      grid: null,
      doQuestion: false,
      doDelete: false,
      questionText: "",
      changed: [],
      showDetail: false,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { createAssetModal } = this.props;
    const prev = prevProps.createAssetModal ?? {};
    if (
      prev.open &&
      !createAssetModal?.open &&
      createAssetModal?.lastCreatedType === "GT"
    ) {
      this.getGameTitles();
      this.props.setCreateAssetModal({ lastCreatedType: null });
    }
  }

  onOperatorChange = (e, { value }) => {
    this.setState(
      {
        operatorId: value,
        manufacturers: [],
        selectedUpdatedFilter: "ALL",
        selectedManufacturerId: "ALL",
        selectedGameTitle: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      },
      () => {
        if (value) {
          this.getGameTitles();
        }
      },
    );
  };

  getAssetTableData = (gameTitleId, statusOption) => {
    const { operatorId } = this.state;

    return getAssetGameLibrary(
      operatorId,
      -1,
      -1,
      -1,
      gameTitleId,
      -1,
      statusOption,
      -1,
      0,
      "#",
      -1,
      -1,
      -1,
      true,
    );
  };

  handleAssetStatusOptionChange = (option) => {
    const { selectedGameTitle } = this.state;
    if (!selectedGameTitle) return;

    this.setState(
      { assetDetailStatusOption: option, loading: true },
      () => {
        this.getAssetTableData(selectedGameTitle.gameTitleId, option)
          .then((tableData) => {
            this.setState({
              assetDetailAssets: tableData.assets || [],
              loading: false,
            });
          })
          .catch(() => this.setState({ loading: false }));
      },
    );
  };

  getGameTitles = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      Promise.all([
        getGameTitleLibraryRaw(operatorId),
        getManufacturersRaw(operatorId),
      ])
        .then(([gameTitlesData, manufacturersData]) => {
          this.setState({
            gameTitles: gameTitlesData,
            manufacturers: manufacturersData,
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  onUpdatedFilterChange = (e, { value }) => {
    this.setState({
      selectedUpdatedFilter: value,
      selectedGameTitle: null,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    });
  };

  onManufacturerFilterChange = (e, { value }) => {
    this.setState({
      selectedManufacturerId: value,
      selectedGameTitle: null,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    });
  };

  handleNew = (selectedOptions, selectedNode, grid = null) => {
    this.setState({
      selectedOptions: selectedOptions.main
        ? selectedOptions
        : { main: selectedOptions, ownership: [], operational: [] },
      changed: [],
      selectedNode,
      grid,
    });
  };

  onSelectRow = (item) => {
    const defaultStatus = ASSET_STATUS_FILTER_DATA[0].value;
    this.setState({
      loading: true,
      showDetail: true,
      assetDetailStatusOption: defaultStatus,
    });
    Promise.all([
      loadGameTitle(item.gameTitleId),
      this.getAssetTableData(item.gameTitleId, defaultStatus),
    ])
      .then(([response, tableData]) => {
        const { doQuestion, doDelete, questionText } = response;
        this.setState(
          {
            doQuestion,
            doDelete,
            questionText,
            selectedGameTitle: item,
            assetDetailAssets: tableData.assets || [],
          },
          () => {
            this.handleNew(
              response.assetUI
                ? response.assetUI
                : { main: response.options, operational: [], ownership: [] },
              item,
              response.grid,
            );
            this.setState({ loading: false });
          },
        );
      })
      .catch(() =>
        this.setState({ loading: false }),
      );
  };

  onNew = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.props.setCreateAssetModal({
      open: true,
      type: "GT",
      operatorId,
      name: "New Game Title",
    });
  };

  onSave = () => {
    const { operatorId, selectedGameTitle, selectedOptions } = this.state;
    const { showSuccessAlert } = this.props;
    if (!operatorId || !selectedGameTitle) return;
    const gameTitleId = selectedGameTitle ? selectedGameTitle.gameTitleId : 0;
    const changed = this.getAll(selectedOptions);

    this.setState({ loading: true }, () => {
      updateGameTitle(gameTitleId, operatorId, changed, () => this.getGameTitles())
        .then(() => {
          this.setState({ loading: false }, () => {
            showSuccessAlert(
              gameTitleId
                ? "Game title updated successfully."
                : "Game title created successfully.",
            );
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  handleDownload = () => {
    const { operatorId, selectedUpdatedFilter, selectedManufacturerId } =
      this.state;
    if (!operatorId) return;
    const isUpdated =
      selectedUpdatedFilter === "ALL" ? -1 : Number(selectedUpdatedFilter);
    const manufacturerId =
      selectedManufacturerId === "ALL" ? -1 : Number(selectedManufacturerId);

    this.setState({ loading: true }, () => {
      gameTitleLibraryDownload(operatorId, isUpdated, manufacturerId).then(
        (response) => {
          this.setState({ loading: false }, () =>
            handleDownload(response),
          );
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  handleAssetDetailDownload = () => {
    const { operatorId, selectedGameTitle, assetDetailStatusOption } =
      this.state;
    if (!operatorId || !selectedGameTitle) return;

    this.setState({ loading: true }, () => {
      getAssetsDetailsDownload(
        operatorId,
        -1,
        -1,
        -1,
        selectedGameTitle.gameTitleId,
        -1,
        assetDetailStatusOption,
        -1,
        0,
        "#",
        -1,
        -1,
        -1,
        true,
      ).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  render() {
    const {
      gameTitles,
      manufacturers,
      loading,
      selectedGameTitle,
      selectedOptions,
      changed,
      operatorId,
      selectedUpdatedFilter,
      selectedManufacturerId,
    } = this.state;

    const canSave =
      !!selectedGameTitle &&
      this.isAllValid(selectedOptions || { main: [], ownership: [], operational: [] }) &&
      changed.length > 0;

    const manufacturerOptions = [
      { key: "all", value: "ALL", text: "All" },
      ...[...manufacturers]
        .sort((a, b) =>
          (a.manufacturerName || "").localeCompare(b.manufacturerName || ""),
        )
        .map((manufacturer) => ({
          key: manufacturer.manufacturerId,
          value: manufacturer.manufacturerId,
          text: manufacturer.manufacturerName,
        })),
    ];

    const filteredGameTitles = gameTitles.filter((title) => {
      const isUpdatedValue = title.isUpdated;
      const matchesUpdatedFilter =
        selectedUpdatedFilter === "ALL" ||
        Number(isUpdatedValue) === Number(selectedUpdatedFilter);

      const matchesManufacturerFilter =
        selectedManufacturerId === "ALL" ||
        Number(title.manufacturerId) === Number(selectedManufacturerId);

      return matchesUpdatedFilter && matchesManufacturerFilter;
    });

    return (
      <div className="flex-stretch gs-flex-container-vertical height-100">
        <Grid className="height-100 flex-stretch">
          <Grid.Row
            className={`flex-stretch ${this.state.showDetail ? "h-50" : "h-100"}`}
          >
            <Grid.Column width={12} className="height-100 flex">
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <div className="mb-0_5">
                <Dropdown
                  placeholder="Select operator"
                  selection
                  options={this.props.operators}
                  value={operatorId || ""}
                  onChange={this.onOperatorChange}
                  disabled={!this.props.operators.length}
                />
                <Dropdown
                  className="ml-1"
                  placeholder="Filter update status"
                  selection
                  options={UPDATED_FILTER_OPTIONS}
                  value={selectedUpdatedFilter}
                  onChange={this.onUpdatedFilterChange}
                  disabled={!operatorId}
                />
                <Dropdown
                  className="ml-1"
                  placeholder="Filter manufacturer"
                  selection
                  options={manufacturerOptions}
                  value={selectedManufacturerId}
                  onChange={this.onManufacturerFilterChange}
                  disabled={!operatorId}
                />
                <Button
                  className="ml-0_5"
                  icon="expand"
                  basic={!this.state.showDetail}
                  color="grey"
                  secondary
                  onClick={() =>
                    this.setState({ showDetail: !this.state.showDetail })
                  }
                  size="mini"
                  disabled={!selectedGameTitle}
                />
              </div>
              <div className="mb-0_5 flex-stretch">
                <AssetMainTable
                  columns={GAME_LIBRARY_TABLE_COLUMNS}
                  data={filteredGameTitles}
                  rowKey="gameTitleId"
                  selectedRowId={selectedGameTitle?.gameTitleId}
                  onSelectRow={this.onSelectRow}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4} className="height-100 flex">
              <div className="flex-start">
                <Button
                  color="green"
                  content="New"
                  onClick={this.onNew}
                  disabled={!operatorId}
                  loading={loading}
                />
                <Button
                  primary
                  content="Save"
                  onClick={this.onSave}
                  disabled={!canSave}
                  loading={loading}
                />
                <DownloadButton
                  disabled={!this.state.operatorId}
                  caption="Export"
                  onClick={this.handleDownload}
                  loading={loading}
                />
              </div>
              <div className="flex-stretch height-100 p-1 border-grey-1 flex">
                <OptionsList
                  options={this.mapChanged(
                    (selectedOptions && selectedOptions.main) || [],
                  )}
                  onChange={this.handleChangeValue}
                />
              </div>
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
            </Grid.Column>
          </Grid.Row>
          {this.state.showDetail && (
            <Grid.Row className="flex-stretch h-50">
              <Grid.Column width={16}>
                <AssetDetailView
                  statusOption={this.state.assetDetailStatusOption}
                  assets={this.state.assetDetailAssets}
                  loading={this.state.loading}
                  operatorId={this.state.operatorId}
                  detailExportDisabled={!this.state.selectedGameTitle}
                  onStatusOptionChange={this.handleAssetStatusOptionChange}
                  onDownloadClick={this.handleAssetDetailDownload}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  operators: state.operators?.filter((x) => x.value !== -1) ?? [],
  createAssetModal: state.createAssetModal,
});

export default connect(mapStateToProps, {
  setCreateAssetModal,
  showSuccessAlert,
})(GameLibraryView);